import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbstractFeatureService } from '@y42/feature';
import { getJSONSchemaURL, METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouterService } from '@y42/feature/routing';
import { GitQuery } from '@y42/git';
import { Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import { StatusAlertAclService } from '../state/status-alert-acl.service';
import { StatusAlertGitService } from '../state/status-alert-git.service';
import { StatusAlertUserDataService } from '../state/status-alert-user-data.service';
import { StatusAlertState, StatusAlertStoreModel, StatusAlertUIModel } from '../state/status-alert.model';
import { StatusAlertQuery } from '../state/status-alert.query';
import { StatusAlertStore } from '../state/status-alert.store';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class StatusAlertService extends AbstractFeatureService<StatusAlertState> {
  constructor(
    protected readonly userData: StatusAlertUserDataService,
    protected readonly store: StatusAlertStore,
    protected readonly query: StatusAlertQuery,
    protected readonly git: StatusAlertGitService,
    protected readonly acl: StatusAlertAclService,
    protected readonly routerService: Y42RouterService,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
  ) {
    super({
      featureType: Y42FeatureType.StatusAlert,
      store,
      query,
      acl,
      userData,
      git,
      gitQuery,
      toast,
      childStores: [],
    });
  }

  async create(alertUi: StatusAlertUIModel): Promise<void> {
    const $name = getSafeString(alertUi.name);
    const alert = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      name: alertUi.name,
      settings: {
        ...alertUi.settings,
        $schema: getJSONSchemaURL('status-alert-settings'),
      } as StatusAlertStoreModel['settings'],
    });

    await this.store.pending;

    await this.git.commitChanges(`status alerts: new alert "${$name}" created`, alert.$path);
  }

  async update(item: Partial<StatusAlertStoreModel>, $path: StatusAlertStoreModel['$path']): Promise<void> {
    this.store.update($path, item);
    await this.store.pending;
    await this.git.commitChanges(`status alerts: alert "${$path}" updated`, $path);
  }

  async delete($path: StatusAlertStoreModel['$path']) {
    const entity = this.query.getEntity($path);
    this.store.delete($path);
    await this.store.pending;
    await this.git.commitChanges(`status alerts: status alert '${entity?.$name ?? $path}' deleted`, [$path]);
  }
}
